import React from "react";
import { BookOpen, AlertTriangle } from "lucide-react";

export default function Metodologia() {
  return (
    <div className="space-y-6" data-testid="metodologia-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Metodología</div>
        <h1 className="section-title mt-1">Lógica del estudio de viabilidad eléctrica</h1>
        <p className="section-sub">
          Descripción técnica de los pasos seguidos para estimar el consumo y la viabilidad eléctrica de cada línea.
        </p>
      </div>

      <div className="crtm-card p-6 flex items-start gap-3 bg-amber-50 border-amber-200">
        <AlertTriangle className="text-amber-700 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-amber-900 leading-relaxed">
          Los resultados mostrados son <strong>estimaciones técnicas</strong> sujetas a validación con datos reales
          de operación, modelos concretos de vehículo, estrategia de carga y condiciones de explotación
          (orografía, climatología, ocupación). No sustituyen a un estudio de detalle con telemetría.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Step n={1} title="Revisión documental">
          Análisis del anteproyecto del operador cargado en la plataforma.
          Se extraen las líneas, sublíneas, itinerarios y tipologías declaradas.
        </Step>
        <Step n={2} title="Identificación de tipologías (Anexo VIII)">
          Para cada línea se asocia la tipología de vehículo prevista (Clase I/II/III/A) y su tamaño (8/10/12/15/18 m)
          según el Anexo VIII del proyecto.
        </Step>
        <Step n={3} title="Caracterización operacional">
          Se incorporan longitud por sentido (Anexo II), expediciones anuales (Anexo V) y veh-km totales por línea.
        </Step>
        <Step n={4} title="Asignación de factores de consumo">
          Cada combinación tamaño × servicio recibe un factor base y conservador (kWh/km), ajustado por
          penalizaciones según tipo de servicio (urbano +8%, nocturno −5%, etc.).
        </Step>
        <Step n={5} title="Definición de escenarios">
          Se definen tres escenarios — <strong>Base</strong>, <strong>Conservador</strong> y <strong>Exigente</strong> — que aplican
          incrementos por climatización, margen de batería, ocupación, penalización urbana y pendiente.
        </Step>
        <Step n={6} title="Estimación de consumo anual">
          Consumo anual (kWh) = veh-km anuales × factor kWh/km. El consumo por expedición y diario se derivan a partir de longitud y operación.
        </Step>
        <Step n={7} title="Score de viabilidad eléctrica (sistema v2, escala 0-10 + letra A+/A/B/C/D)">
          Cada línea recibe un <strong>score normalizado 0-10</strong> calculado como
          <span className="font-mono"> 0,4·F1 + 0,3·F2 + 0,3·F3</span>, donde
          F1 mide la eficiencia energética (kWh/km), F2 la longitud comercial y F3 la
          carga operativa por vehículo en la jornada típica laborable del Anexo V
          (plan de servicio con tiempos de regulación = 0). En base al score se
          asigna una letra:{" "}
          <span className="pill alta">A+ ≥ 9</span>{" "}
          <span className="pill alta">A ≥ 7</span>{" "}
          <span className="pill cond">B ≥ 5</span>{" "}
          <span className="pill cond">C ≥ 3</span>{" "}
          <span className="pill rev">D &lt; 3</span>.
          Se conserva la etiqueta legacy (Alta/Condicionada/A revisar) derivada del score.
        </Step>
        <Step n={8} title="Recomendaciones técnicas">
          Para cada línea se generan observaciones operativas y recomendaciones de estrategia de carga (cochera, oportunidad, pantógrafo o segregación de servicio).
        </Step>
      </div>

      <div className="crtm-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen size={18} />
          <h3 className="font-heading font-semibold text-[15px]">Rangos de consumo por tamaño de vehículo</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="crtm-table">
            <thead>
              <tr>
                <th>Tamaño</th>
                <th>Rango factor kWh/km (urbano)</th>
                <th>Tipología típica</th>
                <th>Observaciones</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>8 m</td><td className="font-mono">0,8 – 1,1</td><td>Clase III / A (microbús)</td><td>Servicios urbanos de bajo flujo, circulares.</td></tr>
              <tr><td>10 m</td><td className="font-mono">1,0 – 1,3</td><td>Clase I / III</td><td>Servicios urbanos medios.</td></tr>
              <tr><td>12 m</td><td className="font-mono">1,2 – 1,6</td><td>Clase I / II</td><td>Vehículo estándar de la red.</td></tr>
              <tr><td>15 m</td><td className="font-mono">1,5 – 2,0</td><td>Clase II rígido largo</td><td>Líneas troncales de alta capacidad.</td></tr>
              <tr><td>18 m</td><td className="font-mono">1,8 – 2,5</td><td>Clase II articulado</td><td>Mayor demanda energética. Requiere batería ampliada.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="crtm-card p-6" data-testid="umbrales-viabilidad-v2">
        <h3 className="font-heading font-semibold text-[15px] mb-1">Sistema de scoring de viabilidad eléctrica (v2)</h3>
        <p className="text-xs text-gray-500 mb-4">
          Score normalizado 0-10 = <span className="font-mono">0,4·F1 + 0,3·F2 + 0,3·F3</span>. Tres componentes con
          interpolación lineal a partir de las anclas indicadas. El plan de servicio se recalcula con
          tiempos de regulación reducidos a 0 minutos para obtener la mínima flota teórica.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm mb-5">
          <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
            <div className="font-semibold text-gray-900 mb-1">F1 · Eficiencia energética <span className="text-gray-500 font-normal">(40 %)</span></div>
            <p className="text-xs text-gray-700 leading-relaxed mb-2">Factor kWh/km base de la línea (sin penalización climatológica).</p>
            <table className="w-full text-xs">
              <tbody>
                <tr><td className="font-mono">≤ 1,0 kWh/km</td><td className="text-right font-semibold">10 pts</td></tr>
                <tr><td className="font-mono">1,5</td><td className="text-right font-semibold">8 pts</td></tr>
                <tr><td className="font-mono">2,0</td><td className="text-right font-semibold">5 pts</td></tr>
                <tr><td className="font-mono">2,5</td><td className="text-right font-semibold">2 pts</td></tr>
                <tr><td className="font-mono">≥ 3,0</td><td className="text-right font-semibold">0 pts</td></tr>
              </tbody>
            </table>
          </div>
          <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
            <div className="font-semibold text-gray-900 mb-1">F2 · Longitud de línea <span className="text-gray-500 font-normal">(30 %)</span></div>
            <p className="text-xs text-gray-700 leading-relaxed mb-2">Longitud comercial OSRM por sentido. A menor longitud, mayor margen de autonomía.</p>
            <table className="w-full text-xs">
              <tbody>
                <tr><td className="font-mono">≤ 10 km</td><td className="text-right font-semibold">10 pts</td></tr>
                <tr><td className="font-mono">20</td><td className="text-right font-semibold">8 pts</td></tr>
                <tr><td className="font-mono">40</td><td className="text-right font-semibold">5 pts</td></tr>
                <tr><td className="font-mono">60</td><td className="text-right font-semibold">3 pts</td></tr>
                <tr><td className="font-mono">≥ 100</td><td className="text-right font-semibold">0 pts</td></tr>
              </tbody>
            </table>
          </div>
          <div className="border border-gray-200 bg-gray-50 rounded-lg p-4">
            <div className="font-semibold text-gray-900 mb-1">F3 · Carga operativa por vehículo <span className="text-gray-500 font-normal">(30 %)</span></div>
            <p className="text-xs text-gray-700 leading-relaxed mb-2">
              Número de recargas necesarias al día (mínimo 1 = carga nocturna en cochera). Se calcula
              como <span className="font-mono">ceil(kWh<sub>veh-día</sub> / batería<sub>utilizable</sub>)</span> usando
              la <strong>batería del vehículo asignado a cada línea</strong> (no la del articulado 18 m). Cuantas más
              recargas intra-jornada requiera la línea, mayor dependencia de infraestructura de oportunidad.
            </p>
            <table className="w-full text-xs">
              <tbody>
                <tr><td className="font-mono">1 recarga/día</td><td className="text-right font-semibold">10 pts</td></tr>
                <tr><td className="font-mono">2</td><td className="text-right font-semibold">7 pts</td></tr>
                <tr><td className="font-mono">3</td><td className="text-right font-semibold">4 pts</td></tr>
                <tr><td className="font-mono">4</td><td className="text-right font-semibold">2 pts</td></tr>
                <tr><td className="font-mono">≥ 5</td><td className="text-right font-semibold">0 pts</td></tr>
              </tbody>
            </table>
            <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
              Interpretación operativa: <strong>1</strong> = solo carga nocturna · <strong>2</strong> = 1 recarga
              oportunidad en cabecera (típico urbano) · <strong>3</strong> = 2 recargas (interurbano) ·
              <strong> 4</strong> = necesita pantógrafo o cambio de bus · <strong>≥ 5</strong> = infraestructura extensiva.
            </p>
          </div>
        </div>

        <h4 className="font-heading font-semibold text-sm mb-2">Umbrales de clasificación (letra)</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-5">
          <div className="border rounded-lg p-3" style={{ borderColor: "#14532D33", background: "#14532D11" }}>
            <div className="text-2xl font-heading font-semibold" style={{ color: "#14532D" }}>A+</div>
            <div className="text-[11px] text-gray-700 mt-1">Score ≥ 9</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Viabilidad muy alta</div>
          </div>
          <div className="border rounded-lg p-3" style={{ borderColor: "#15803D33", background: "#15803D11" }}>
            <div className="text-2xl font-heading font-semibold" style={{ color: "#15803D" }}>A</div>
            <div className="text-[11px] text-gray-700 mt-1">7 ≤ Score &lt; 9</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Electrificable con carga en cochera</div>
          </div>
          <div className="border rounded-lg p-3" style={{ borderColor: "#CA8A0433", background: "#CA8A0411" }}>
            <div className="text-2xl font-heading font-semibold" style={{ color: "#CA8A04" }}>B</div>
            <div className="text-[11px] text-gray-700 mt-1">5 ≤ Score &lt; 7</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Requiere estrategia mixta</div>
          </div>
          <div className="border rounded-lg p-3" style={{ borderColor: "#B4530933", background: "#B4530911" }}>
            <div className="text-2xl font-heading font-semibold" style={{ color: "#B45309" }}>C</div>
            <div className="text-[11px] text-gray-700 mt-1">3 ≤ Score &lt; 5</div>
            <div className="text-[11px] text-gray-500 mt-0.5">Carga de oportunidad obligatoria</div>
          </div>
          <div className="border rounded-lg p-3" style={{ borderColor: "#991B1B33", background: "#991B1B11" }}>
            <div className="text-2xl font-heading font-semibold" style={{ color: "#991B1B" }}>D</div>
            <div className="text-[11px] text-gray-700 mt-1">Score &lt; 3</div>
            <div className="text-[11px] text-gray-500 mt-0.5">A revisar — estudio de detalle</div>
          </div>
        </div>

        <h4 className="font-heading font-semibold text-sm mb-2">Capacidad de batería utilizable por tamaño de vehículo</h4>
        <div className="overflow-x-auto mb-3">
          <table className="crtm-table">
            <thead>
              <tr>
                <th>Tamaño</th>
                <th className="text-right">Batería utilizable (kWh)</th>
                <th>Observaciones</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>8 m</td><td className="text-right font-mono">180</td><td>Microbús eléctrico (descontado 15 % de HVAC + degradación + reserva).</td></tr>
              <tr><td>10 m</td><td className="text-right font-mono">250</td><td>Bus medio urbano.</td></tr>
              <tr><td>12 m</td><td className="text-right font-mono">350</td><td>Estándar urbano de la red.</td></tr>
              <tr><td>15 m</td><td className="text-right font-mono">425</td><td>Rígido largo de alta capacidad.</td></tr>
              <tr><td>18 m</td><td className="text-right font-mono">550</td><td>Articulado — habitualmente requiere oportunidad o pantógrafo.</td></tr>
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-gray-500 leading-relaxed">
          La etiqueta legacy <span className="pill alta">Alta</span>{" "}
          <span className="pill cond">Condicionada</span>{" "}
          <span className="pill rev">A revisar</span> se conserva en paralelo,
          derivada directamente del score (≥ 7 Alta · ≥ 4 Condicionada · &lt; 4 A revisar) para
          preservar compatibilidad con Dashboard, Inversión y Escenarios.
        </p>
      </div>
    </div>
  );
}

const Step = ({ n, title, children }) => (
  <div className="crtm-card p-5">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#14532D] text-white font-heading font-bold flex items-center justify-center flex-shrink-0">
        {n}
      </div>
      <div>
        <h3 className="font-heading font-semibold text-[15px]">{title}</h3>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">{children}</p>
      </div>
    </div>
  </div>
);
